import type { City, PricingPlanField } from "@/apis/city/city-apis";
import { useCity, usePricingPlanFields } from "@/v2/queries";
import {
  type BaseEntityModalResult,
  createEntityModalHook,
} from "./createEntityModalHook";

const useBaseCityModal = createEntityModalHook<City, number>({
  useQueryHook: useCity,
  getEntityId: (city) => city.id,
});

export interface UseCityModalResult
  extends Omit<BaseEntityModalResult<City, number>, "entity"> {
  city: City | null;
  pricingPlanFields: PricingPlanField[];
  isLoadingDropdowns: boolean;
}

/**
 * Hook for managing city modal state and data loading.
 *
 * Handles:
 * - Modal visibility state
 * - Loading city data via TanStack Query
 * - Loading pricing plan fields for dropdowns
 * - Modal opens as soon as city loads (prevents stale cached data)
 */
export function useCityModal(): UseCityModalResult {
  const baseModal = useBaseCityModal();
  const { data: pricingPlanFieldsResponse, isLoading: isLoadingFields } =
    usePricingPlanFields();

  return {
    isOpen: baseModal.isOpen,
    city: baseModal.entity,
    isLoading: baseModal.isLoading,
    pricingPlanFields: pricingPlanFieldsResponse?.fields ?? [],
    open: baseModal.open,
    close: baseModal.close,
    isLoadingDropdowns: isLoadingFields,
  };
}
